import { Component, computed, inject, OnInit, Pipe, PipeTransform, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';

import { GAME_ID } from '../global/game-id';
import { AppEvent, EVENT_ORGANISER_ROLE } from './models';
import { EventService, EventListFilter } from './event.service';
import { COUNTRY_OPTIONS } from '../admin/form-controls/country-select/countries';
import { UserService } from '../global/userService/user-service';
import { ImageService } from '../global/images/image.service';

@Pipe({ name: 'eventPoints', standalone: true })
export class EventPointsPipe implements PipeTransform {
    transform(event: AppEvent): string {
        const slots = (event.listSlots || []).filter((s) => s.pointsLimit != null);
        if (slots.length === 0) return '';
        if (slots.length === 1) return `${slots[0].pointsLimit}pts`;
        const values = slots.map((s) => s.pointsLimit as number);
        const min = Math.min(...values);
        const max = Math.max(...values);
        if (min === max) return `${min}pts`;
        return `${min}–${max}pts`;
    }
}

@Component({
    standalone: false,
    selector: 'abs-event-list',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.MENU_ITEMS.EVENTS' | translate">
            <div class="buttons">
                <ion-button *role="organiserRole" [routerLink]="['create']">
                    {{ 'GLOBAL.EVENTS.CREATE_EVENT' | translate }}
                </ion-button>
                @if (!isOrganiser()) {
                    <ion-button fill="outline" [routerLink]="['apply-organiser']">
                        {{ 'GLOBAL.EVENTS.APPLY_TO_ORGANISE' | translate }}
                    </ion-button>
                }
            </div>

            <ion-segment [value]="activeTab()" (ionChange)="setTab($any($event.detail.value))">
                <ion-segment-button value="all">
                    <ion-label>{{ 'GLOBAL.EVENTS.TAB_ALL' | translate }}</ion-label>
                </ion-segment-button>
                <ion-segment-button value="mine">
                    <ion-label>{{ 'GLOBAL.EVENTS.TAB_MY_EVENTS' | translate }}</ion-label>
                </ion-segment-button>
            </ion-segment>

            @if (activeTab() === 'all') {
                <ion-card class="filters">
                    <ion-card-content>
                        <form>
                            <div class="form-row">
                                <ion-input
                                    [value]="filters.title || ''"
                                    (ionInput)="setFilter('title', $event.detail.value)"
                                    [placeholder]="'GLOBAL.EVENTS.FILTER_TITLE_PLACEHOLDER' | translate"
                                    clearInput
                                ></ion-input>
                            </div>
                            <div class="form-row">
                                <ion-item lines="none">
                                    <ion-toggle
                                        [checked]="nearMeEnabled()"
                                        [disabled]="nearMeLoading()"
                                        (ionChange)="toggleNearMe($event.detail.checked)"
                                    >
                                        {{ 'GLOBAL.EVENTS.FILTER_NEAR_ME' | translate }}
                                    </ion-toggle>
                                    @if (nearMeLoading()) {
                                        <ion-spinner name="dots"></ion-spinner>
                                    }
                                </ion-item>
                                <p class="near-me-caveat">{{ 'GLOBAL.EVENTS.FILTER_NEAR_ME_CAVEAT' | translate }}</p>
                                @if (nearMeError()) {
                                    <p class="near-me-error">{{ nearMeError() | translate }}</p>
                                }
                            </div>
                            <div class="form-row">
                                <ion-item lines="none">
                                    <ion-toggle
                                        [checked]="includePastEvents()"
                                        (ionChange)="toggleIncludePast($event.detail.checked)"
                                    >
                                        {{ 'GLOBAL.EVENTS.FILTER_INCLUDE_PAST' | translate }}
                                    </ion-toggle>
                                </ion-item>
                            </div>
                            <div class="horizontal">
                                <div class="form-row">
                                    <ion-label>{{ 'GLOBAL.EVENTS.FILTER_COUNTRY' | translate }}</ion-label>
                                    <ion-select
                                        interface="popover"
                                        [interfaceOptions]="{ cssClass: 'popover-wide' }"
                                        [value]="filters.countryCode || ''"
                                        (ionChange)="setFilter('countryCode', $event.detail.value)"
                                        [placeholder]="'GLOBAL.EVENTS.FILTER_COUNTRY_PLACEHOLDER' | translate"
                                    >
                                        <ion-select-option value="">{{
                                            'GLOBAL.EVENTS.FILTER_ALL_COUNTRIES' | translate
                                        }}</ion-select-option>
                                        @for (c of countryOptions; track c.value) {
                                            <ion-select-option [value]="c.value">{{ c.label }}</ion-select-option>
                                        }
                                    </ion-select>
                                </div>
                                <div class="form-row">
                                    <ion-label>{{ 'GLOBAL.EVENTS.FILTER_FROM' | translate }}</ion-label>
                                    <ion-input
                                        type="date"
                                        [value]="filters.startDateFrom || ''"
                                        (ionChange)="setFilter('startDateFrom', $event.detail.value)"
                                    ></ion-input>
                                </div>
                                <div class="form-row">
                                    <ion-label>{{ 'GLOBAL.EVENTS.FILTER_TO' | translate }}</ion-label>
                                    <ion-input
                                        type="date"
                                        [value]="filters.startDateTo || ''"
                                        (ionChange)="setFilter('startDateTo', $event.detail.value)"
                                    ></ion-input>
                                </div>
                            </div>
                        </form>
                    </ion-card-content>
                </ion-card>

                @if (events$ | async; as events) {
                    @if (events.length === 0) {
                        <p>{{ 'GLOBAL.EVENTS.NO_EVENTS' | translate }}</p>
                    } @else {
                        <div class="gutter-padding">
                            <div class="tile-grid">
                                @for (event of events; track event._id) {
                                    <ng-container *ngTemplateOutlet="eventCard; context: { event: event }"></ng-container>
                                }
                            </div>
                        </div>
                    }
                }
            }

            @if (activeTab() === 'mine') {
                @if (myEvents$ | async; as myEvents) {
                    @if (myEvents.length === 0) {
                        <p>{{ 'GLOBAL.EVENTS.NO_MY_EVENTS' | translate }}</p>
                    } @else {
                        <div class="gutter-padding">
                            <div class="tile-grid">
                                @for (event of myEvents; track event._id) {
                                    <ng-container *ngTemplateOutlet="eventCard; context: { event: event }"></ng-container>
                                }
                            </div>
                        </div>
                    }
                }
            }

            <ng-template #eventCard let-event="event">
                <div class="tile tile--interactive event-card" [routerLink]="[event._id]">
                    <!-- Always rendered, so cards in a grid row are the same shape whether
                         or not the event has a banner. -->
                    <div class="event-card-media" [class.event-card-media--empty]="!event.imageId">
                        @if (event.imageId) {
                            <img [src]="imageService.url(event.imageId)" alt="" class="event-card-img" />
                        } @else {
                            <ion-icon name="calendar-outline"></ion-icon>
                        }
                    </div>
                    <div class="event-card-body">
                        <div class="tile-title">
                            {{ event.title }}
                            @if (event.organiserId === currentUserId()) {
                                <ion-badge color="tertiary">{{ 'GLOBAL.EVENTS.ORGANISER_BADGE' | translate }}</ion-badge>
                            }
                        </div>
                        <div class="tile-subtitle">
                            @if (event.location?.venueName) {
                                {{ event.location.venueName }},&nbsp;
                            }
                            @if (event.location?.city) {
                                {{ event.location.city }},&nbsp;
                            }
                            {{ event.location?.countryCode | countryName }} &mdash; {{ event.startDate | date: 'd MMM y' }}
                        </div>
                        <div class="tile-actions">
                            @if (event.organiserId === currentUserId()) {
                                <ion-badge [color]="event.publishStatus | publishStatusColour">{{
                                    event.publishStatus | publishStatusKey | translate
                                }}</ion-badge>
                            }
                            @let pts = event | eventPoints;
                            <span class="tile-subtitle">
                                @if (pts) {
                                    {{ pts }} &mdash;
                                }
                                {{ 'GLOBAL.EVENTS.ROUNDS_COUNT' | translate: { count: event.numberOfRounds } }}
                            </span>
                        </div>
                    </div>
                </div>
            </ng-template>
        </abs-page-layout>
    `,
    // Styles live in army-builder-shared/src/lib/global/styles/events.scss, per the
    // workspace rule against CSS in component files.
})
export class EventListPage implements OnInit {
    private eventService = inject(EventService);
    userService = inject(UserService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    gameId = inject(GAME_ID);
    imageService = inject(ImageService);

    readonly countryOptions = COUNTRY_OPTIONS;

    activeTab = signal<'all' | 'mine'>('all');
    filters: EventListFilter = {};
    private filters$ = new BehaviorSubject<EventListFilter>({});

    nearMeEnabled = signal(false);
    nearMeError = signal<string | null>(null);
    nearMeLoading = signal(false);
    private nearMeRequestId = 0;

    includePastEvents = signal(false);

    events$: Observable<AppEvent[]>;
    myEvents$: Observable<AppEvent[]>;

    readonly currentUserId = computed(() => this.userService.user()?.userId);
    readonly organiserRole = EVENT_ORGANISER_ROLE;
    readonly isOrganiser = computed(() => (this.userService.user()?.roles || []).includes(EVENT_ORGANISER_ROLE));

    ngOnInit() {
        this.events$ = this.filters$.pipe(switchMap((filters) => this.eventService.listEvents(this.gameId, filters)));
        this.myEvents$ = this.userService.loggedIn$.pipe(
            switchMap((loggedIn) => (loggedIn ? this.eventService.listMyEvents(this.gameId) : of([])))
        );
    }

    setTab(tab: 'all' | 'mine') {
        this.activeTab.set(tab);
    }

    setFilter(key: keyof EventListFilter, value: string) {
        this.filters = { ...this.filters, [key]: value !== '' ? value : undefined };
        this.filters$.next(this.filters);
    }

    toggleIncludePast(checked: boolean) {
        this.includePastEvents.set(checked);
        this.filters = { ...this.filters, includePast: checked || undefined };
        this.filters$.next(this.filters);
    }

    toggleNearMe(enabled: boolean) {
        if (!enabled) {
            // Invalidate any in-flight request so a stale callback can't resurrect this filter.
            this.nearMeRequestId++;
            this.nearMeLoading.set(false);
            this.nearMeEnabled.set(false);
            this.nearMeError.set(null);
            this.filters = { ...this.filters, lat: undefined, lng: undefined, radiusKm: undefined };
            this.filters$.next(this.filters);
            return;
        }

        if (!navigator.geolocation) {
            this.nearMeError.set('GLOBAL.EVENTS.FILTER_NEAR_ME_UNAVAILABLE');
            return;
        }

        const requestId = ++this.nearMeRequestId;
        this.nearMeLoading.set(true);
        this.nearMeEnabled.set(true);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                if (requestId !== this.nearMeRequestId) return;
                this.nearMeLoading.set(false);
                this.nearMeEnabled.set(true);
                this.nearMeError.set(null);
                this.filters = {
                    ...this.filters,
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    radiusKm: 160
                };
                this.filters$.next(this.filters);
            },
            () => {
                if (requestId !== this.nearMeRequestId) return;
                this.nearMeLoading.set(false);
                this.nearMeEnabled.set(false);
                this.nearMeError.set('GLOBAL.EVENTS.FILTER_NEAR_ME_DENIED');
            },
            { timeout: 10000, maximumAge: 300000 }
        );
    }

}
